import type {
  AttachedContext,
  AttachedContextRef,
  ChatSurface,
  PendingAttachment
} from '@Ai/components/chatTypes';
import { useSyncExternalStore } from 'react';

import { randomUuid } from '@/lib/randomUuid';

export type PageSelectionContext = {
  tag_name: string;
  component_name: string | null;
  visible_text: string | null;
  source_label: string | null;
  source_snippet: string | null;
  inline_edit_unavailable_reason: string | null;
  line: number | null;
  rect: {
    top: number;
    left: number;
    width: number;
    height: number;
  } | null;
};

export type PageAskAiRequest = {
  id: string;
  surface: ChatSurface;
  prompt: string;
  /** Files the requester already uploaded. Only the finished `temp_path` travels. */
  attachments?: PendingAttachment[];
  attachedContext?: AttachedContext | null;
};

/**
 * A message started for the user rather than sent for them: the composer gets the
 * text and a removable context chip, and the user finishes and sends it.
 */
export type PageComposeRequest = {
  id: string;
  surface: ChatSurface;
  text: string;
  ref: AttachedContextRef;
};

export type PageRecoveryHandoff = {
  surface: ChatSurface;
  page_id: string;
  page_label: string;
  recovery_id: string;
  recovery_label: string;
};

export type ScenarioRepairHandoff = {
  surface: ChatSurface;
  workflow_id: string;
  workflow_label: string;
  scenario_id: string;
  scenario_label: string;
};

export const PAGE_RECOVERY_CONTEXT_KIND = 'page_recovery';

export type PlaygroundSnippetHandoff = {
  surface: ChatSurface;
  snippet_id: string;
  example_label: string;
};

/** Mirrors PlaygroundSnippet::ATTACHED_CONTEXT_KIND, which the harness reads to build the task. */
export const PLAYGROUND_SNIPPET_CONTEXT_KIND = 'page_playground_snippet';

/** Mirrors WorkflowScenario::ATTACHED_CONTEXT_KIND, which the harness reads to build the task. */
export const WORKFLOW_SCENARIO_RUN_CONTEXT_KIND = 'workflow_scenario_run';

type PageAssistantHandoffState = {
  selectionContext: PageSelectionContext | null;
  askAiRequest: PageAskAiRequest | null;
  composeRequest: PageComposeRequest | null;
};

const SERVER_STATE: PageAssistantHandoffState = {
  selectionContext: null,
  askAiRequest: null,
  composeRequest: null
};

let state: PageAssistantHandoffState = SERVER_STATE;

const listeners = new Set<() => void>();

function setState(partial: Partial<PageAssistantHandoffState>): void {
  state = { ...state, ...partial };
  listeners.forEach((listener) => listener());
}

function makeRequestId(): string {
  return randomUuid();
}

export const pageAssistantHandoffStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot(): PageAssistantHandoffState {
    return state;
  },
  getServerSnapshot(): PageAssistantHandoffState {
    return SERVER_STATE;
  },
  setSelectionContext(selectionContext: PageSelectionContext | null): void {
    setState({ selectionContext });
  },
  requestAssistantEdit(request: Omit<PageAskAiRequest, 'id'> & { id?: string }): void {
    setState({
      askAiRequest: {
        ...request,
        id: request.id ?? makeRequestId()
      }
    });
  },
  // The backup travels as an attached-context ref, never as message text: the
  // ref carries identifiers only, the harness turns it into the recovery task,
  // and the customer sees a plain sentence with a chip naming the backup.
  requestPageRecovery(request: PageRecoveryHandoff): void {
    setState({
      askAiRequest: {
        id: makeRequestId(),
        surface: request.surface,
        prompt: `Combine the useful changes from the backup "${request.recovery_label}" with the current draft of this page.`,
        attachedContext: {
          focus: { kind: 'page', id: request.page_id, label: request.page_label },
          selections: [
            {
              kind: PAGE_RECOVERY_CONTEXT_KIND,
              id: request.recovery_id,
              label: request.recovery_label
            }
          ]
        }
      }
    });
  },
  // The ref carries the stored snippet id and the harness turns it into the integration task, as
  // for a page backup. Unlike a backup, the example usually needs the builder's own words — where
  // it goes, which data it shows — so it is composed rather than sent: the builder edits the
  // sentence and sends it. The example source is never written into the message.
  requestPlaygroundSnippet(request: PlaygroundSnippetHandoff): void {
    setState({
      composeRequest: {
        id: makeRequestId(),
        surface: request.surface,
        text: `Add the "${request.example_label}" example from the Page Playground to this page. `,
        ref: {
          kind: PLAYGROUND_SNIPPET_CONTEXT_KIND,
          id: request.snippet_id,
          label: request.example_label
        }
      }
    });
  },
  compose(request: Omit<PageComposeRequest, 'id'>): void {
    setState({ composeRequest: { ...request, id: makeRequestId() } });
  },
  consumeComposeRequest(id: string): void {
    if (state.composeRequest?.id === id) {
      setState({ composeRequest: null });
    }
  },
  // Same contract as the page backup above: the ref carries the scenario id, the harness turns it
  // into the repair task with the run's own evidence, and the tester sees a plain sentence with a
  // chip naming the scenario. None of the failure is written into the message.
  requestScenarioRepair(request: ScenarioRepairHandoff): void {
    setState({
      askAiRequest: {
        id: makeRequestId(),
        surface: request.surface,
        prompt: `Repair the failing scenario "${request.scenario_label}".`,
        attachedContext: {
          focus: {
            kind: 'workflow',
            id: request.workflow_id,
            label: request.workflow_label
          },
          selections: [
            {
              kind: WORKFLOW_SCENARIO_RUN_CONTEXT_KIND,
              id: request.scenario_id,
              label: request.scenario_label
            }
          ]
        }
      }
    });
  },
  consumeAssistantEditRequest(id: string): void {
    if (state.askAiRequest?.id === id) {
      setState({ askAiRequest: null });
    }
  },
  reset(): void {
    setState(SERVER_STATE);
  }
};

export function usePageAssistantHandoff(): PageAssistantHandoffState {
  return useSyncExternalStore(
    pageAssistantHandoffStore.subscribe,
    pageAssistantHandoffStore.getSnapshot,
    pageAssistantHandoffStore.getServerSnapshot
  );
}
